import { useEffect, useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Flame, Users, Archive, Settings, Download, Trash2, Plus, ArrowRight, X, Heart, Shield, Eye, EyeOff,
  ChevronLeft, ChevronRight, Copy, Check
} from 'lucide-react'
import { Toaster, toast } from 'sonner'
import { format } from 'date-fns'

import type { Reflection, PublicEmber, Resonance, ForkChoice, ValuesFingerprintParams } from './lib/types'
import { FORK_LABELS, FORK_SHORT } from './lib/types'
import { generateFingerprintSVG, svgToPngDataUrl, deriveReciprocityBalance } from './lib/fingerprint'
import { formatRelative, generateEmberSeed, hashSeed, getStoredSeed, storeSeed, clearLocal, cn } from './lib/utils'

// === Creator links (prominent as required) ===
const LINKEDIN = 'https://www.linkedin.com/in/mindful-abhay/'
const COFFEE = 'https://buymeacoffee.com/abhaypratapsingh'

// === Worker location (set VITE_WORKER_URL in prod after deploy) ===
const WORKER_URL = (import.meta.env.VITE_WORKER_URL as string) || ''
function api(path: string) {
  if (WORKER_URL) return `${WORKER_URL}${path}`
  return path
}

type View = 'fire' | 'offer' | 'signals' | 'fingerprint' | 'coals' | 'archive' | 'ethics'

// The four signals we deliberately capture
interface SignalState {
  friction: string
  cooperation: number          // 0–100
  cooperationNote: string
  reciprocityOffered: string
  reciprocityReceived: string
  reciprocityNote: string
  fork: ForkChoice
  forkNote: string
  normNamed: string
  shareToCoals: boolean
  allowAi: boolean
  telemetryConsent: boolean
}

const DEFAULT_SIGNALS: SignalState = {
  friction: '',
  cooperation: 55,
  cooperationNote: '',
  reciprocityOffered: '',
  reciprocityReceived: '',
  reciprocityNote: '',
  fork: 'contextual_grace',
  forkNote: '',
  normNamed: '',
  shareToCoals: false,
  allowAi: true,
  telemetryConsent: true,
}

const ETHICS_BANNER = "This practice sits alongside human care. It does not replace conversations with people who know you, therapists, friends, elders, or your own body’s wisdom."

export default function Embers() {
  // Identity (pseudonymous, seed only)
  const [seed, setSeed] = useState<string | null>(getStoredSeed())
  const [userHash, setUserHash] = useState<string>('')
  const [userId, setUserId] = useState<string | null>(null)

  // Navigation
  const [view, setView] = useState<View>('fire')
  const [previousView, setPreviousView] = useState<View>('fire')

  // Core ritual state
  const [signals, setSignals] = useState<SignalState>(DEFAULT_SIGNALS)
  const [currentReflectionId, setCurrentReflectionId] = useState<string | null>(null)
  const [aiSpark, setAiSpark] = useState<string>('')
  const [isRequestingSpark, setIsRequestingSpark] = useState(false)
  const [fingerprintSvg, setFingerprintSvg] = useState<string>('')
  const [isBanking, setIsBanking] = useState(false)

  // Data
  const [myReflections, setMyReflections] = useState<Reflection[]>([])
  const [liveCoals, setLiveCoals] = useState<PublicEmber[]>([])
  const [activeCoalsEmber, setActiveCoalsEmber] = useState<PublicEmber | null>(null)
  const [resonances, setResonances] = useState<Resonance[]>([])
  const [newResonance, setNewResonance] = useState('')

  // Privacy surface
  const [showEthics, setShowEthics] = useState(false)

  // === Identity bootstrap (seed never leaves device except as hash) ===
  async function ensureIdentity() {
    let currentSeed = seed
    if (!currentSeed) {
      const { seed: newSeed } = generateEmberSeed()
      currentSeed = newSeed
      storeSeed(newSeed)
      setSeed(newSeed)
    }
    const h = await hashSeed(currentSeed)
    setUserHash(h)

    // Bootstrap / get or create user on the server
    const res = await fetch(api('/api/identity'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ seed_hash: h }),
    })
    if (res.ok) {
      const data = await res.json()
      setUserId(data.user_id)
      localStorage.setItem('embers:userId', data.user_id)
    }
    return h
  }

  async function loadMyReflections() {
    const h = userHash || await ensureIdentity()
    const res = await fetch(api('/api/reflections'), {
      headers: { 'Authorization': `Ember ${h}` },
    })
    if (res.ok) {
      const data = await res.json()
      setMyReflections(data.reflections || [])
    }
  }

  async function loadLiveCoals() {
    const res = await fetch(api('/api/coals'))
    if (res.ok) {
      const data = await res.json()
      setLiveCoals(data.coals || [])
    }
  }

  useEffect(() => {
    ensureIdentity().then(() => {
      loadMyReflections()
      loadLiveCoals()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // === Ritual flow controls ===
  function startOffering() {
    setSignals(DEFAULT_SIGNALS)
    setAiSpark('')
    setFingerprintSvg('')
    setCurrentReflectionId(null)
    setView('offer')
  }

  function goToSignals() {
    if (!signals.friction.trim()) {
      toast.error('Offer the friction first — even a changed-name version.')
      return
    }
    setView('signals')
  }

  function updateSignal<K extends keyof SignalState>(key: K, value: SignalState[K]) {
    setSignals(prev => ({ ...prev, [key]: value }))
  }

  // === Generate the Values Fingerprint (client-side, instant, beautiful) ===
  function buildFingerprintParams(): ValuesFingerprintParams {
    const recipBalance = deriveReciprocityBalance(signals.reciprocityOffered, signals.reciprocityReceived)
    return {
      cooperation: signals.cooperation / 100,
      fork: signals.fork,
      reciprocityBalance: recipBalance,
      normWeight: Math.min(0.95, Math.max(0.3, (signals.normNamed.trim().length / 42) + 0.25)),
      normText: signals.normNamed.trim() || 'the norm at stake',
    }
  }

  function renderFingerprint() {
    const params = buildFingerprintParams()
    const svg = generateFingerprintSVG(params, 520)
    setFingerprintSvg(svg)
  }

  useEffect(() => {
    if (view === 'fingerprint') {
      renderFingerprint()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, signals.cooperation, signals.fork, signals.reciprocityOffered, signals.reciprocityReceived, signals.normNamed])

  // === Optional AI Spark (explicit, consent-gated, humble) ===
  async function requestSpark() {
    if (!signals.allowAi) {
      toast('AI sparks are disabled for this reflection.')
      return
    }
    setIsRequestingSpark(true)
    try {
      const h = userHash || await ensureIdentity()
      const payload = {
        cooperation: signals.cooperation / 100,
        fork: signals.fork,
        reciprocityOffered: signals.reciprocityOffered,
        reciprocityReceived: signals.reciprocityReceived,
        normNamed: signals.normNamed,
        shortContext: signals.friction.slice(0, 420),
      }
      const res = await fetch(api('/api/spark'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Ember ${h}` },
        body: JSON.stringify(payload),
      })
      const data = await res.json()
      if (data.spark) {
        setAiSpark(data.spark)
        toast.success('Spark received. You can rewrite every word.')
      } else {
        toast('The fire was quiet. You can still write your own reflection.')
      }
    } catch {
      toast.error('Spark unavailable right now. Your words are enough.')
    } finally {
      setIsRequestingSpark(false)
    }
  }

  // === Bank the ember (core action) ===
  async function bankEmber(alsoSaveArtifact: boolean) {
    if (!signals.normNamed.trim()) {
      toast.error('Name the norm that feels important. It is the center of the fingerprint.')
      return
    }
    setIsBanking(true)
    try {
      const h = userHash || await ensureIdentity()
      const params = buildFingerprintParams()

      const body = {
        friction_text: signals.friction.trim(),
        cooperation_threshold: signals.cooperation / 100,
        cooperation_note: signals.cooperationNote.trim(),
        reciprocity_offered: signals.reciprocityOffered.trim(),
        reciprocity_received: signals.reciprocityReceived.trim(),
        reciprocity_imbalance_note: signals.reciprocityNote.trim(),
        fork_choice: signals.fork,
        fork_note: signals.forkNote.trim(),
        norm_named: signals.normNamed.trim(),
        ai_spark: aiSpark || null,
        share_to_coals: signals.shareToCoals ? 1 : 0,
        allow_ai: signals.allowAi ? 1 : 0,
        telemetry_consent: signals.telemetryConsent ? 1 : 0,
        create_artifact: alsoSaveArtifact,
        fingerprint_params: params,
      }

      const res = await fetch(api('/api/reflections'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Ember ${h}` },
        body: JSON.stringify(body),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed')

      setCurrentReflectionId(data.reflection_id)

      // If artifact requested, the worker returns a key; we can also generate a local PNG for immediate download
      if (alsoSaveArtifact && fingerprintSvg) {
        try {
          const png = await svgToPngDataUrl(fingerprintSvg)
          const a = document.createElement('a')
          a.href = png
          a.download = `embers-fingerprint-${Date.now()}.png`
          document.body.appendChild(a)
          a.click()
          a.remove()
        } catch {}
      }

      await loadMyReflections()
      await loadLiveCoals()

      toast.success('Ember banked. The signal is kept.')
      setView('fingerprint')
    } catch (e: any) {
      toast.error(e.message || 'Could not bank right now.')
    } finally {
      setIsBanking(false)
    }
  }

  // === Open a past ember from archive ===
  function reopenEmber(r: Reflection) {
    setSignals({
      friction: r.friction_text,
      cooperation: Math.round(r.cooperation_threshold * 100),
      cooperationNote: r.cooperation_note || '',
      reciprocityOffered: r.reciprocity_offered || '',
      reciprocityReceived: r.reciprocity_received || '',
      reciprocityNote: r.reciprocity_imbalance_note || '',
      fork: r.fork_choice,
      forkNote: r.fork_note || '',
      normNamed: r.norm_named,
      shareToCoals: !!r.share_to_coals,
      allowAi: !!r.allow_ai,
      telemetryConsent: !!r.telemetry_consent,
    })
    setAiSpark(r.ai_spark || '')
    setCurrentReflectionId(r.id)
    const params: ValuesFingerprintParams = {
      cooperation: r.cooperation_threshold,
      fork: r.fork_choice,
      reciprocityBalance: deriveReciprocityBalance(r.reciprocity_offered || '', r.reciprocity_received || ''),
      normWeight: 0.7,
      normText: r.norm_named,
    }
    setFingerprintSvg(generateFingerprintSVG(params))
    setView('fingerprint')
  }

  // === Shared coals (the signal fire) ===
  async function openCoalsEmber(ember: PublicEmber) {
    setActiveCoalsEmber(ember)
    setNewResonance('')
    const res = await fetch(api(`/api/coals/${ember.id}/resonances`))
    if (res.ok) {
      const data = await res.json()
      setResonances(data.resonances || [])
    }
    setView('coals')
  }

  async function layCoal() {
    if (!activeCoalsEmber || !newResonance.trim()) return
    const h = userHash || await ensureIdentity()
    const res = await fetch(api(`/api/coals/${activeCoalsEmber.id}/resonate`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Ember ${h}` },
      body: JSON.stringify({ body: newResonance.trim().slice(0, 240) }),
    })
    if (res.ok) {
      const data = await res.json()
      setResonances(prev => [...prev, data.resonance])
      setNewResonance('')
      toast('Coal laid. The fire is tended.')
      await loadLiveCoals()
    } else {
      toast.error('Could not lay the coal.')
    }
  }

  // === Privacy & deletion ===
  async function purgeEverything() {
    if (!confirm('This will permanently delete all your reflections and resonances from the server. Your local seed will also be cleared. Continue?')) return
    const h = userHash || await ensureIdentity()
    await fetch(api('/api/purge'), {
      method: 'POST',
      headers: { 'Authorization': `Ember ${h}` },
    })
    clearLocal()
    setSeed(null)
    setUserId(null)
    setMyReflections([])
    setLiveCoals([])
    setView('fire')
    toast('Everything burned. You are a new ember.')
    // fresh identity on next action
  }

  async function toggleGlobalConsent(field: 'share' | 'ai' | 'telemetry', value: boolean) {
    // Per-reflection for now — global note is shown in ethics. We keep it lightweight.
    toast('Consent is captured per ember. You can change it on each past reflection from your archive.')
  }

  function go(viewName: View) {
    setPreviousView(view)
    setView(viewName)
  }

  // === Derived ===
  const canBank = signals.normNamed.trim().length > 2
  const liveCoalsCount = liveCoals.length
  const myCount = myReflections.length

  // === Render ===
  return (
    <div className="min-h-screen bg-[#121110] text-[#F5F0E6] selection:bg-[#E07A3D] selection:text-[#121110]">
      {/* Warm, minimal, ritual header */}
      <header className="sticky top-0 z-50 border-b border-[#2C2825] bg-[#121110]/95 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-5 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-2xl bg-[#E07A3D] flex items-center justify-center">
                <Flame size={18} className="text-[#121110]" />
              </div>
              <div>
                <div className="font-semibold tracking-[-0.8px] text-xl text-[#F5F0E6]">embers</div>
                <div className="text-[9px] text-[#8A8178] -mt-1 tracking-[1.5px]">SIGNAL FIRE • SHAME RESILIENCE</div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1 text-sm">
            <button onClick={() => go('fire')} className={cn('nav-link', view === 'fire' && 'active')}>The Fire</button>
            <button onClick={() => go('coals')} className={cn('nav-link', view === 'coals' && 'active')}><Users size={15} className="mr-1 inline -mt-px"/>Coals <span className="opacity-40">({liveCoalsCount})</span></button>
            <button onClick={() => go('archive')} className={cn('nav-link', view === 'archive' && 'active')}><Archive size={15} className="mr-1 inline -mt-px"/>My Embers <span className="opacity-40">({myCount})</span></button>
            <button onClick={() => setShowEthics(true)} className="nav-link text-[#8A8178] hover:text-[#F5F0E6]"><Shield size={15}/></button>
          </div>

          <div className="flex items-center gap-3 text-xs">
            <a href={LINKEDIN} target="_blank" rel="noreferrer" className="hidden sm:block underline underline-offset-2 text-[#8A8178] hover:text-[#F5F0E6]">Abhay on LinkedIn</a>
            <a href={COFFEE} target="_blank" rel="noreferrer" className="hidden sm:block underline underline-offset-2 text-[#8A8178] hover:text-[#F5F0E6]">Buy me a coffee</a>
            <button onClick={purgeEverything} className="text-[#8A8178] hover:text-[#E07A3D] flex items-center gap-1" title="Right to be forgotten"><Trash2 size={14}/></button>
          </div>
        </div>
      </header>

      {/* Permanent ethical reminder — never hidden, never cute */}
      <div className="border-b border-[#2C2825] bg-[#1C1917]">
        <div className="max-w-5xl mx-auto px-5 py-2 text-[12px] text-[#8A8178] flex items-center gap-2">
          <Shield size={13} className="shrink-0" />
          <span>{ETHICS_BANNER}</span>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 pt-9 pb-24">
        {/* THE FIRE — landing / ritual entry */}
        {view === 'fire' && (
          <div className="max-w-3xl">
            <div className="mb-8">
              <div className="uppercase tracking-[3px] text-xs text-[#E07A3D] mb-2">A COMMUNITY SIGNAL FIRE</div>
              <h1 className="text-[52px] leading-[0.96] tracking-[-3.2px] font-semibold text-[#F5F0E6] mb-4">
                Turn text friction<br />into knowing.
              </h1>
              <p className="text-[17px] text-[#B8AFA3] max-w-[52ch]">
                You bring a misread message, a spiraling comment thread, an email that landed wrong.
                Together we surface the cooperation you extended, the reciprocity you felt (or didn’t), the fork you stood at, and the norm you want to protect.
              </p>
            </div>

            <div className="flex flex-wrap gap-3 mb-9">
              <button onClick={startOffering} className="btn btn-primary h-14 px-9 text-base flex items-center gap-2">
                <Plus size={18} /> Offer a friction to the fire
              </button>
              <button onClick={() => go('coals')} className="btn h-14 px-8 text-base flex items-center gap-2">
                <Users size={17} /> See the live coals
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="card p-5">
                <div className="text-[#E07A3D] text-xs tracking-widest mb-2">WHAT WE CAPTURE</div>
                <div className="space-y-1.5 text-[#B8AFA3]">
                  <div>• Cooperation threshold</div>
                  <div>• Reciprocity response</div>
                  <div>• The punishment / repair / grace / boundary fork</div>
                  <div>• The norm you felt was being negotiated</div>
                </div>
              </div>
              <div className="card p-5">
                <div className="text-[#E07A3D] text-xs tracking-widest mb-2">WHAT COMES OUT</div>
                <div className="text-[#B8AFA3]">
                  A personal <span className="text-[#F5F0E6]">Values Fingerprint</span> — a visual and textual record you can return to, rewrite, and keep. Optional shared coals so others can say “I see this too.”
                </div>
              </div>
              <div className="card p-5 border-[#E07A3D]/30">
                <div className="text-[#E07A3D] text-xs tracking-widest mb-2">WHAT THIS IS NOT</div>
                <div className="text-[#B8AFA3]">
                  A productivity tool. A reputation fixer. A replacement for the people who actually care about you. An AI that tells you what to do.
                </div>
              </div>
            </div>

            <div className="mt-10 text-[11px] text-[#6B6259]">
              Built with Cloudflare Pages, Workers, D1, R2 (opt-in), Workers AI (opt-in sparks only), and AI Gateway for observability. All data capture is first-party and consent-based.
            </div>
          </div>
        )}

        {/* OFFER THE FRICTION */}
        {view === 'offer' && (
          <div className="max-w-3xl">
            <button onClick={() => go('fire')} className="text-xs flex items-center gap-1 mb-5 text-[#8A8178] hover:text-[#F5F0E6]"><ChevronLeft size={15}/> back</button>

            <div className="section-title">OFFER THE FRICTION</div>
            <h2 className="text-4xl tracking-[-1.6px] leading-none mb-3">What text interaction has been sitting in your body?</h2>
            <p className="text-[#B8AFA3] mb-6 max-w-prose">Change every name. Change the platform. You are not here to be judged and neither is anyone else. This is raw material for your own clarity.</p>

            <textarea
              className="input min-h-[138px] text-[15px] leading-snug"
              placeholder="Example: A colleague replied “k” to a long update I sent about a project that matters to me. I spent the next 90 minutes convinced I had done something wrong and that my reputation inside the team was quietly collapsing."
              value={signals.friction}
              onChange={e => updateSignal('friction', e.target.value)}
            />

            <div className="mt-6 space-y-4">
              <label className="flex items-start gap-3 text-sm cursor-pointer">
                <input type="checkbox" checked={signals.shareToCoals} onChange={e => updateSignal('shareToCoals', e.target.checked)} className="mt-1" />
                <span className="text-[#B8AFA3]">Share an anonymized version of this reflection (never the raw text) to the community signal fire so others can lay coals on it and I can feel less alone in how these things land.</span>
              </label>

              <label className="flex items-start gap-3 text-sm cursor-pointer">
                <input type="checkbox" checked={signals.allowAi} onChange={e => updateSignal('allowAi', e.target.checked)} className="mt-1" />
                <span className="text-[#B8AFA3]">I may request one optional AI reflection spark later. I understand it is a mirror, not truth, and I will rewrite or discard it.</span>
              </label>

              <label className="flex items-start gap-3 text-sm cursor-pointer">
                <input type="checkbox" checked={signals.telemetryConsent} onChange={e => updateSignal('telemetryConsent', e.target.checked)} className="mt-1" />
                <span className="text-[#B8AFA3]">Contribute the structural signals (cooperation %, fork choice, norm category) — never the text — to help surface patterns in how text spaces create shame. Revocable anytime.</span>
              </label>
            </div>

            <div className="mt-8 flex gap-3">
              <button onClick={() => go('fire')} className="btn">Cancel</button>
              <button onClick={goToSignals} className="btn btn-primary flex-1 justify-center">Continue to the four signals <ArrowRight size={17}/></button>
            </div>
            <div className="text-[10px] text-[#6B6259] mt-4">Nothing is sent until you explicitly bank the ember at the end.</div>
          </div>
        )}

        {/* THE FOUR SIGNALS — the real heart of the app */}
        {view === 'signals' && (
          <div className="max-w-3xl">
            <button onClick={() => go('offer')} className="text-xs flex items-center gap-1 mb-4 text-[#8A8178]"><ChevronLeft size={15}/> back to the text</button>

            <div className="section-title">THE FOUR SIGNALS</div>
            <h2 className="text-4xl tracking-[-1.7px] mb-2">Add logs to the fire deliberately.</h2>
            <p className="text-[#B8AFA3] mb-8">Slow down. These are not quiz answers. They are the material from which your fingerprint will be made.</p>

            {/* 1. Cooperation Threshold */}
            <div className="card p-7 mb-6">
              <div className="uppercase text-xs tracking-[1.5px] text-[#E07A3D] mb-2">1 — COOPERATION THRESHOLD</div>
              <div className="text-lg leading-tight mb-4">How far did you extend the benefit of the doubt before it started costing you your own ground?</div>

              <div className="flex items-center gap-4 mb-2">
                <div className="text-xs text-[#8A8178] w-12">guarded</div>
                <input
                  type="range" min={0} max={100} step={1}
                  value={signals.cooperation}
                  onChange={e => updateSignal('cooperation', parseInt(e.target.value))}
                  className="flex-1 accent-[#E07A3D]"
                />
                <div className="text-xs text-[#8A8178] w-12 text-right">full trust</div>
              </div>
              <div className="text-center font-mono text-sm text-[#E07A3D] mb-4">{signals.cooperation}%</div>

              <textarea className="input min-h-[78px]" placeholder="What was the exact moment the threshold was crossed for you?" value={signals.cooperationNote} onChange={e => updateSignal('cooperationNote', e.target.value)} />
            </div>

            {/* 2. Reciprocity Response */}
            <div className="card p-7 mb-6">
              <div className="uppercase text-xs tracking-[1.5px] text-[#E07A3D] mb-2">2 — RECIPROCITY RESPONSE</div>
              <div className="text-lg leading-tight mb-4">What did you offer in the space between you, and what actually came back?</div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-[#8A8178] mb-1.5">What I offered (labor, grace, explanation, silence, vulnerability…)</div>
                  <textarea className="input min-h-[92px]" value={signals.reciprocityOffered} onChange={e => updateSignal('reciprocityOffered', e.target.value)} />
                </div>
                <div>
                  <div className="text-xs text-[#8A8178] mb-1.5">What I received or felt was missing</div>
                  <textarea className="input min-h-[92px]" value={signals.reciprocityReceived} onChange={e => updateSignal('reciprocityReceived', e.target.value)} />
                </div>
              </div>
              <div className="mt-3">
                <textarea className="input min-h-[58px]" placeholder="How did the imbalance sit in your body or reputation story?" value={signals.reciprocityNote} onChange={e => updateSignal('reciprocityNote', e.target.value)} />
              </div>
            </div>

            {/* 3. The Fork */}
            <div className="card p-7 mb-6">
              <div className="uppercase text-xs tracking-[1.5px] text-[#E07A3D] mb-2">3 — THE FORK</div>
              <div className="text-lg leading-tight mb-3">At some point you stood at a real choice. Which path did you take, or wish you had taken?</div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                {(Object.keys(FORK_LABELS) as ForkChoice[]).map(f => (
                  <button
                    key={f}
                    onClick={() => updateSignal('fork', f)}
                    className={cn(
                      'text-left px-4 py-3 rounded-xl border text-sm transition-all',
                      signals.fork === f ? 'border-[#E07A3D] bg-[#1C1917]' : 'border-[#2C2825] hover:border-[#463F38]'
                    )}
                  >
                    {FORK_LABELS[f]}
                  </button>
                ))}
              </div>

              <textarea className="input min-h-[72px]" placeholder="I chose X. I wish I had chosen Y because…" value={signals.forkNote} onChange={e => updateSignal('forkNote', e.target.value)} />
              <div className="text-[11px] text-[#6B6259] mt-2">There is no correct fork. Only the one that protects your integrity and future capacity.</div>
            </div>

            {/* 4. Norm Emergence */}
            <div className="card p-7 mb-6">
              <div className="uppercase text-xs tracking-[1.5px] text-[#E07A3D] mb-2">4 — NORM EMERGENCE</div>
              <div className="text-lg leading-tight mb-3">What expectation, value, or unspoken rule was being negotiated or violated here?</div>
              <input
                className="input text-lg"
                placeholder="e.g. that people will ask for clarification before assuming coldness or malice"
                value={signals.normNamed}
                onChange={e => updateSignal('normNamed', e.target.value)}
              />
              <div className="text-[11px] text-[#6B6259] mt-2">This becomes the quiet center of your fingerprint.</div>
            </div>

            <div className="flex gap-3">
              <button onClick={() => go('offer')} className="btn">Back</button>
              <button onClick={() => { renderFingerprint(); go('fingerprint') }} className="btn btn-primary flex-1 justify-center">See the Values Fingerprint <ArrowRight /></button>
            </div>
          </div>
        )}

        {/* THE VALUES FINGERPRINT + SPARK + BANK */}
        {view === 'fingerprint' && (
          <div className="max-w-[620px] mx-auto">
            <button onClick={() => go('signals')} className="text-xs flex items-center gap-1 mb-4 text-[#8A8178]"><ChevronLeft size={15}/> back to signals</button>

            <div className="section-title">YOUR VALUES FINGERPRINT</div>
            <h2 className="text-3xl tracking-[-1.3px] mb-2">This is yours. Return to it when the story in your head gets loud.</h2>

            {/* The actual visual */}
            <div className="my-6 rounded-3xl overflow-hidden border border-[#2C2825] bg-[#0D0C0B] p-4">
              {fingerprintSvg ? (
                <div dangerouslySetInnerHTML={{ __html: fingerprintSvg }} />
              ) : (
                <div className="h-[520px] flex items-center justify-center text-[#8A8178]">The fingerprint will appear here…</div>
              )}
            </div>

            {/* Optional humble AI spark */}
            <div className="card p-6 mb-6">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#E07A3D]">OPTIONAL AI MIRROR</div>
                  <div className="text-sm text-[#8A8178]">A reflection spark. Always editable. Never directive.</div>
                </div>
                <button onClick={requestSpark} disabled={isRequestingSpark || !signals.allowAi} className="btn text-xs">
                  {isRequestingSpark ? 'Listening to the fire…' : 'Request a spark'}
                </button>
              </div>

              {aiSpark ? (
                <textarea className="input min-h-[92px] text-[14.5px]" value={aiSpark} onChange={e => setAiSpark(e.target.value)} />
              ) : (
                <div className="text-xs text-[#6B6259]">No spark yet. You can write your own below or bank without one.</div>
              )}
              <div className="text-[10px] text-[#6B6259] mt-2">This text is generated by Workers AI through AI Gateway. It can be wrong. It is a prompt for your own thinking, not a verdict.</div>
            </div>

            {/* Quick editable summary of the four signals */}
            <div className="text-xs uppercase tracking-[1px] text-[#8A8178] mb-2">WHAT THIS FINGERPRINT ENCODES</div>
            <div className="card p-5 text-sm space-y-3 mb-6">
              <div><span className="text-[#8A8178]">Cooperation:</span> {signals.cooperation}% — {signals.cooperationNote || '—'}</div>
              <div><span className="text-[#8A8178]">Reciprocity felt:</span> Offered “{signals.reciprocityOffered || '…'}” / Received “{signals.reciprocityReceived || '…'}”</div>
              <div><span className="text-[#8A8178]">Fork taken:</span> {FORK_LABELS[signals.fork]} {signals.forkNote && `— ${signals.forkNote}`}</div>
              <div><span className="text-[#8A8178]">Norm at stake:</span> {signals.normNamed}</div>
            </div>

            {/* Bank actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={() => bankEmber(false)} disabled={!canBank || isBanking} className="btn flex-1 justify-center h-12 text-base">
                {isBanking ? 'Banking…' : 'Bank this ember (private)'}
              </button>
              <button onClick={() => bankEmber(true)} disabled={!canBank || isBanking} className="btn btn-primary flex-1 justify-center h-12 text-base">
                Bank + save fingerprint as artifact
              </button>
            </div>
            <div className="text-center text-[11px] text-[#6B6259] mt-3">You can always reopen, rewrite, and re-export from My Embers.</div>
          </div>
        )}

        {/* THE SHARED COALS — community signal fire */}
        {view === 'coals' && (
          <div>
            <button onClick={() => go(previousView === 'coals' ? 'fire' : previousView)} className="text-xs flex items-center gap-1 mb-4 text-[#8A8178]"><ChevronLeft size={15}/> back</button>

            <div className="section-title">THE LIVE COALS</div>
            <h2 className="text-4xl tracking-[-1.6px] mb-2">Other people’s embers. Small recognitions only.</h2>
            <p className="text-[#B8AFA3] max-w-prose mb-6">Raw friction text is never shown here. Only the norm named, the fork chosen, and a short context the person chose to offer the commons. Lay a coal if something resonates. No threads, no pile-ons.</p>

            {liveCoals.length === 0 && (
              <div className="empty-state">No live coals right now. Be the first to share one from your next reflection.</div>
            )}

            <div className="space-y-3">
              {liveCoals.map(ember => (
                <button key={ember.id} onClick={() => openCoalsEmber(ember)} className="w-full text-left card p-5 hover:border-[#463F38] transition-colors">
                  <div className="flex justify-between items-start gap-4">
                    <div>
                      <div className="text-sm text-[#E07A3D] mb-1 tracking-wide">{FORK_SHORT[ember.fork_choice].toUpperCase()} • {Math.round(ember.cooperation_threshold * 100)}% cooperation</div>
                      <div className="text-[15px] leading-tight mb-2">{ember.norm_named}</div>
                      <div className="text-[#B8AFA3] text-sm line-clamp-2">{ember.shared_context}</div>
                    </div>
                    <div className="text-right text-xs text-[#6B6259] shrink-0">{formatRelative(ember.created_at)}<br/>{ember.resonance_count} coals</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* SINGLE COAL VIEW + RESONANCE LAYING */}
        {view === 'coals' && activeCoalsEmber && (
          <div className="fixed inset-0 bg-black/80 z-[70] flex items-center justify-center p-4" onClick={() => { setActiveCoalsEmber(null); setView('coals') }}>
            <div className="card w-full max-w-xl p-6" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between">
                <div>
                  <div className="uppercase tracking-[1px] text-xs text-[#E07A3D]">{FORK_LABELS[activeCoalsEmber.fork_choice]}</div>
                  <div className="text-xl mt-1">{activeCoalsEmber.norm_named}</div>
                </div>
                <button onClick={() => { setActiveCoalsEmber(null); setView('coals') }}><X /></button>
              </div>

              <div className="my-4 text-[#B8AFA3]">{activeCoalsEmber.shared_context}</div>

              <div className="border-t border-[#2C2825] pt-4 mt-2">
                <div className="text-xs text-[#8A8178] mb-2">Lay a small coal (one or two sentences of recognition or lived difference)</div>
                <div className="flex gap-2">
                  <input className="input flex-1" placeholder="I also chose withdraw and it protected me for a season." value={newResonance} onChange={e => setNewResonance(e.target.value)} />
                  <button onClick={layCoal} className="btn btn-primary">Lay</button>
                </div>
              </div>

              <div className="mt-5 space-y-2 text-sm">
                {resonances.length === 0 && <div className="text-xs text-[#6B6259]">No coals yet. Yours would be the first.</div>}
                {resonances.map(r => (
                  <div key={r.id} className="text-[#B8AFA3] border-l-2 border-[#2C2825] pl-3">“{r.body}” <span className="text-[#6B6259] text-xs ml-2">— {formatRelative(r.created_at)}</span></div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* MY EMBERS ARCHIVE */}
        {view === 'archive' && (
          <div className="max-w-3xl">
            <div className="section-title">MY EMBERS</div>
            <h2 className="text-4xl tracking-[-1.6px] mb-2">The record you can return to.</h2>
            <p className="text-[#B8AFA3] mb-6">Reopen any past reflection. Rewrite the words. Re-generate or redraw the fingerprint. Export the whole thing. Delete it forever.</p>

            {myReflections.length === 0 && <div className="empty-state">No embers banked yet. Offer your first friction.</div>}

            <div className="space-y-3">
              {myReflections.map(r => (
                <div key={r.id} className="card p-5 flex flex-col sm:flex-row gap-4 items-start">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-[#E07A3D] mb-1">{format(new Date(r.created_at), 'yyyy-MM-dd')} • {FORK_SHORT[r.fork_choice]} • {Math.round(r.cooperation_threshold * 100)}%</div>
                    <div className="font-medium mb-1 line-clamp-1">{r.norm_named}</div>
                    <div className="text-sm text-[#B8AFA3] line-clamp-2">{r.friction_text}</div>
                    {r.share_to_coals ? <div className="text-[10px] mt-1 text-[#E07A3D]/70">Contributed to shared coals</div> : null}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => reopenEmber(r)} className="btn text-xs">Reopen</button>
                    {r.artifact_r2_key && <button className="btn text-xs" onClick={() => toast('Artifact is in your R2 bucket (key logged on creation).')}>Artifact</button>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Prominent footer links */}
      <footer className="border-t border-[#2C2825] py-9 text-xs text-[#8A8178]">
        <div className="max-w-5xl mx-auto px-5 flex flex-wrap gap-x-6 gap-y-1 items-center">
          <span>Made with care by Abhay Pratap Singh.</span>
          <a href={LINKEDIN} target="_blank" className="underline underline-offset-2 hover:text-[#F5F0E6]">LinkedIn — mindful-abhay</a>
          <a href={COFFEE} target="_blank" className="underline underline-offset-2 hover:text-[#F5F0E6]">Buy me a coffee</a>
          <button onClick={() => setShowEthics(true)} className="underline underline-offset-2 hover:text-[#F5F0E6]">Ethics &amp; your data</button>
          <span className="text-[#6B6259]">Free tier. Consent first. Human care always.</span>
        </div>
      </footer>

      {/* Ethics / Privacy modal */}
      <AnimatePresence>
        {showEthics && (
          <div className="fixed inset-0 bg-black/80 z-[80] flex items-center justify-center p-5" onClick={() => setShowEthics(false)}>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="card max-w-2xl p-7 text-sm" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-start mb-4">
                <div className="font-semibold text-lg tracking-tight">How Embers works with your mind and your data</div>
                <button onClick={() => setShowEthics(false)}><X /></button>
              </div>

              <div className="space-y-4 text-[#B8AFA3]">
                <p>{ETHICS_BANNER}</p>

                <div>
                  <div className="text-[#F5F0E6] mb-1">What is stored</div>
                  <ul className="list-disc pl-5 space-y-0.5">
                    <li>Your local random seed (never sent). Only its hash reaches the server.</li>
                    <li>The full friction text and all four signals — private to you by default.</li>
                    <li>If you choose “share to coals”, a short context + the structural signals + the norm + fork become visible (anonymized) to others.</li>
                    <li>Opt-in R2 artifacts only when you explicitly press “save as artifact”.</li>
                  </ul>
                </div>

                <div>
                  <div className="text-[#F5F0E6] mb-1">AI usage</div>
                  <p>AI sparks are completely optional, per-ember, and routed through Cloudflare AI Gateway so the operator can see errors and token use without the content being used to train external models. The output is always shown as editable text with heavy disclaimers. You can ignore it entirely.</p>
                </div>

                <div>
                  <div className="text-[#F5F0E6] mb-1">Telemetry</div>
                  <p>The four signals + fork + a coarse category of the norm you named are the only things that can ever be written to Analytics Engine — and only when you have the telemetry consent box checked for that ember. No text bodies ever leave for telemetry.</p>
                </div>

                <div>
                  <div className="text-[#F5F0E6] mb-1">Deletion</div>
                  <p>The “burn everything” button in the header deletes your rows from D1 and removes your ability to be associated with past resonances. R2 artifacts you created are your responsibility to delete from the bucket (or ask the operator).</p>
                </div>
              </div>

              <div className="mt-6 text-xs text-[#6B6259]">This is a practice for people who live part of their lives in text and want to stay intact. It is deliberately not a product that tries to own your relationships or your reputation.</div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <Toaster position="top-center" richColors closeButton className="toast" />
    </div>
  )
}
